import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentTypeLabel } from '../../common/enum/ContentTypeLabel';
import { getWatermark, Watermark } from '../../localStorage/watermark';
import { TFunction } from 'i18next';

export interface Position {
  id: number;
  name: string;
  value: string;
}

export interface Rotation {
  id: number;
  name: string;
  value: number;
}

export interface Color {
  id: number;
  name: string;
  value: string;
}

export const getPositions = (t: TFunction): Position[] => [
  { id: 1, name: t('adjustWatermark.positions.topLeft'), value: 'TopLeft' },
  { id: 2, name: t('adjustWatermark.positions.topRight'), value: 'TopRight' },
  { id: 3, name: t('adjustWatermark.positions.center'), value: 'Center' },
  { id: 4, name: t('adjustWatermark.positions.bottomLeft'), value: 'BottomLeft' },
  { id: 5, name: t('adjustWatermark.positions.bottomRight'), value: 'BottomRight' }
];

export const rotations: Rotation[] = [
  { id: 1, name: '0', value: 0 },
  { id: 2, name: '45', value: 45 },
  { id: 3, name: '90', value: 90 }
];

export const getColors = (t: TFunction): Color[] => [
  { id: 1, name: t('adjustWatermark.colors.red'), value: '#EB4034' },
  { id: 2, name: t('adjustWatermark.colors.gray'), value: '#A3A0A0' }
];

interface AdjustWatermarkProps {
  open: boolean;
  contentType: string;
  accessLevelName?: string;
  username?: string;
  onClose?: () => void;
}

export default function AdjustWatermark({
  open = false,
  contentType,
  accessLevelName = 'SCG Confidential',
  username = `Backspace User`,
  onClose
}: AdjustWatermarkProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [watermarks, setWatermarks] = useState<Watermark[]>([]);

  const tradeSecretLabel = accessLevelName.toUpperCase();
  const actionByLabel = t('adjustWatermark.downloadBy', {
    username,
    date: moment().format('DD-MMM-YYYY')
  });

  const isPdfFile = Boolean(contentType === ContentTypeLabel.pdf);

  useEffect(() => {
    if (open) {
      const watermarks = getWatermark();
      if (watermarks) {
        setWatermarks(
          watermarks.map((watermark: Watermark) => {
            let position = watermark.position;
            let rotation = watermark.rotation;
            let text = watermark.text;
            if (watermark.name === 'tradeSecretLabel') {
              text = tradeSecretLabel;
            }
            if (watermark.name === 'actionByLabel') {
              text = actionByLabel;
            }
            if (contentType === ContentTypeLabel.word) {
              position = { id: 3, name: t('adjustWatermark.positions.center'), value: 'Center' };
              rotation = { id: 2, name: '45', value: 45 };
            }
            if (contentType === ContentTypeLabel.excel) {
              position =
                watermark.name === 'tradeSecretLabel'
                  ? { id: 1, name: t('adjustWatermark.positions.topLeft'), value: 'TopLeft' }
                  : { id: 2, name: t('adjustWatermark.positions.topRight'), value: 'TopRight' };
              rotation = { id: 1, name: '0', value: 0 };
            }
            return {
              ...watermark,
              text,
              position,
              rotation
            };
          })
        );
      } else {
        const defaultWatermarks = [
          {
            name: 'tradeSecretLabel',
            text: tradeSecretLabel,
            position:
              contentType === ContentTypeLabel.excel
                ? { id: 1, name: t('adjustWatermark.positions.topLeft'), value: 'TopLeft' }
                : { id: 3, name: t('adjustWatermark.positions.center'), value: 'Center' },
            rotation:
              contentType === ContentTypeLabel.excel
                ? { id: 1, name: '0', value: 0 }
                : { id: 2, name: '45', value: 45 },
            color: { id: 1, name: t('adjustWatermark.colors.red'), value: '#EB4034' }
          },
          {
            name: 'actionByLabel',
            text: actionByLabel,
            position:
              contentType === ContentTypeLabel.excel
                ? { id: 2, name: t('adjustWatermark.positions.topRight'), value: 'TopRight' }
                : { id: 3, name: t('adjustWatermark.positions.center'), value: 'Center' },
            rotation:
              contentType === ContentTypeLabel.excel
                ? { id: 1, name: '0', value: 0 }
                : { id: 2, name: '45', value: 45 },
            color: { id: 1, name: t('adjustWatermark.colors.red'), value: '#EB4034' }
          }
        ];
        setWatermarks(defaultWatermarks);
      }
    }
  }, [open, contentType]);

  function handleChangeValue(
    name: string,
    key: string,
    newValue: Position | Rotation | null
  ) {
    // Field mustn't empty because position field need other field to filter duplicate position
    if (newValue) {
      const watermarkId = watermarks.findIndex(
        (watermark) => watermark.name === name
      );
      let newWatermark = { ...watermarks[watermarkId], [key]: newValue };

      // Reset rotation to 0 degree if position isn't change to Center
      if (
        ((newValue.value === 'TopLeft' ||
          newValue.value === 'TopRight' ||
          newValue.value === 'BottomLeft' ||
          newValue.value === 'BottomRight') &&
          newWatermark.rotation.value === 45) ||
        (newValue.value === 'Center' && newWatermark.rotation.value === 90)
      ) {
        newWatermark = {
          ...newWatermark,
          rotation: { id: 1, name: '0', value: 0 }
        };
      }

      const newWatermarks = [
        ...watermarks.slice(0, watermarkId),
        newWatermark,
        ...watermarks.slice(watermarkId + 1)
      ];
      setWatermarks(newWatermarks);

      // Save watermark settings to local storage
      localStorage.setItem('watermark', JSON.stringify(newWatermarks));
    }
  }

  function handleClose() {
    if (onClose) onClose();
  }

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle className={classes.dialogTitle}>
        {t('adjustWatermark.title')}
      </DialogTitle>
      <DialogContent>
        {watermarks.map((watermark) => {
          return (
            <div key={watermark.name}>
              <Typography gutterBottom>{watermark.text}</Typography>
              <Grid className={classes.mb1} container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    id="position"
                    // Filter duplicate position out from all position field
                    options={getPositions(t).filter((position) =>
                      watermarks
                        .filter(
                          (watermarkEachItem) =>
                            watermarkEachItem.name !== watermark.name
                        )
                        .every(
                          (watermarkEachItem) =>
                            watermarkEachItem.position.value === 'Center' ||
                            watermarkEachItem.position.id !== position.id
                        )
                    )}
                    getOptionLabel={(option) => option?.name}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    value={watermark.position}
                    onChange={(event, newValue) =>
                      handleChangeValue(watermark.name, 'position', newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('adjustWatermark.labels.position') as string}
                        required
                      />
                    )}
                    disabled={!isPdfFile}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    id="rotation"
                    // Filter rotation 45 degree out if position isn't Center and Filter rotation 90 degree out if position is Center
                    options={rotations.filter(
                      (rotation) =>
                        (watermark.position.value === 'Center' ||
                          rotation.value !== 45) &&
                        (watermark.position.value !== 'Center' ||
                          rotation.value !== 90)
                    )}
                    getOptionLabel={(option) => option?.name}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    value={watermark.rotation}
                    onChange={(event, newValue) =>
                      handleChangeValue(watermark.name, 'rotation', newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('adjustWatermark.labels.rotation') as string}
                        required
                      />
                    )}
                    disabled={!isPdfFile}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    id="color"
                    options={getColors(t)}
                    getOptionLabel={(option) => option?.name}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    value={watermark.color}
                    onChange={(event, newValue) =>
                      handleChangeValue(watermark.name, 'color', newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('adjustWatermark.labels.color') as string}
                        required
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          fullWidth
        >
          {t('adjustWatermark.buttons.done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      paddingBottom: theme.spacing(0.5)
    },
    mb1: {
      marginBottom: theme.spacing(1)
    }
  })
);
